import { Button, Card, Menu, Tag, Text } from "@ttc3k/trekker";
import { CalendarItem, CalendarItemType } from "../useCalendarView";
import { Flex } from "styled-system/jsx";
import { NavArrowRight } from "iconoir-react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { sva } from "styled-system/css";
import { NavLink } from "react-router";
import { getCalendarItemTitles } from "./utils";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";

type CalendarItemCardProps = {
  item: CalendarItem;
  allProductsCount: number;
  updateHoveredDateRangeISO: (
    start?: string,
    end?: string,
    isOpen?: boolean,
  ) => void;
  onClosureClick: (closureId: string) => void;
  onEventSaleClick?: (item: CalendarItem) => void;
  onAllEventAttendeesClick?: (item: CalendarItem) => void;
  hasMultipleEventSessions?: boolean;
};

const calendarItemCardRecipe = sva({
  slots: ["surface", "title", "subtitle"],
  base: {
    surface: {
      width: "full",
      bg: "white",
    },
    title: { color: "text.dark" },
    subtitle: { color: "text.mid" },
  },
  variants: {
    visual: {
      ORDER: {},
      EVENT_SALE: {
        surface: {
          borderColor: "border.highlight",
          bg: "bg.highlight.lightest",
        },
      },
      CLOSURE: {
        surface: {
          borderColor: "bg.warning.mid",
          bg: "bg.warning.lightest",
        },
        title: {
          color: "text.warning.dark",
        },
        subtitle: {
          color: "text.warning.mid",
        },
      },
    },
    cancelled: {
      true: {
        surface: {
          borderStyle: "dashed",
          borderColor: "bg.error.dark",
        },
      },
      false: {},
    },
    awaitingApproval: {
      true: {
        surface: {
          borderStyle: "dashed",
          borderColor: "bg.warning.mid",
          bg: "bg.warning.lightest",
        },
      },
      false: {},
    },
  },
});

export const CalendarItemCard = ({
  item,
  allProductsCount,
  updateHoveredDateRangeISO,
  onClosureClick,
  onEventSaleClick,
  onAllEventAttendeesClick,
  hasMultipleEventSessions = false,
}: CalendarItemCardProps) => {
  const { t } = useTranslation("storefront", { keyPrefix: "CALENDAR" });
  const { basePath } = useStorefrontView();
  const { title, subtitle } = getCalendarItemTitles(item, t, allProductsCount);

  const isCancelledOrder =
    item.type === CalendarItemType.Order && !!item.cancelled;
  const isAwaitingApproval =
    item.type === CalendarItemType.Order && !!item.awaitingApproval;

  const isEventSoldOut =
    item.type === CalendarItemType.EventSale &&
    !item.isUnlimited &&
    item.remainingCapacity != null &&
    item.remainingCapacity <= 0;

  const isEventLow =
    item.type === CalendarItemType.EventSale &&
    !item.isUnlimited &&
    !isEventSoldOut &&
    item.remainingCapacity != null &&
    item.remainingCapacity <= 5;

  const getEventSaleTagVisual = (): "destructive" | "warning" | "highlight" => {
    if (isEventSoldOut) return "destructive";
    if (isEventLow) return "warning";
    return "highlight";
  };

  const getEventSaleTagLabel = (): string => {
    if (isEventSoldOut) return t("ITEMS.SOLD_OUT_TAG");
    if (isEventLow)
      return t("ITEMS.REMAINING_TAG", { count: item.remainingCapacity ?? 0 });
    return t("ITEMS.EVENT_SALE_TAG");
  };

  const classes = calendarItemCardRecipe({
    visual: item.type as "ORDER" | "CLOSURE" | "EVENT_SALE",
    cancelled: isCancelledOrder,
    awaitingApproval: isAwaitingApproval,
  });

  return (
    <Card.Surface
      className={classes.surface}
      onMouseEnter={() =>
        updateHoveredDateRangeISO(
          item.startDate,
          item.endDate,
          isCancelledOrder,
        )
      }
      onMouseLeave={() => updateHoveredDateRangeISO()}
    >
      <Card.Body>
        <Card.BodyContent alignItems={"center"}>
          <Flex flexDir={"column"} gap={"3px"}>
            <Text visual={"bodySemiBold"} className={classes.title}>
              {title}
            </Text>
            <Text visual={"smallMedium"} className={classes.subtitle}>
              {subtitle}
            </Text>
          </Flex>

          <Flex gap={"150"} alignItems={"center"}>
            {item.type === CalendarItemType.EventSale ? (
              <Tag visual={getEventSaleTagVisual()}>
                {getEventSaleTagLabel()}
              </Tag>
            ) : (
              <Tag
                visual={
                  isCancelledOrder
                    ? "destructive"
                    : isAwaitingApproval
                      ? "warning"
                      : item.type === CalendarItemType.Order
                      ? "success"
                      : item.type === CalendarItemType.Closure
                        ? "warning"
                        : "highlight"
                }
              >
                {isCancelledOrder
                  ? t("ITEMS.CANCELLED_TAG")
                  : isAwaitingApproval
                    ? t("ITEMS.AWAITING_APPROVAL_TAG")
                    : t("ITEMS." + item.type + "_TAG")}
              </Tag>
            )}
            {item.type === CalendarItemType.Order ? (
              <NavLink to={`${basePath}/order/${item._id}`}>
                <Button mode={"light"} Icon={NavArrowRight} size={"md"} />
              </NavLink>
            ) : item.type === CalendarItemType.Closure ? (
              <Button
                mode={"light"}
                Icon={NavArrowRight}
                size={"md"}
                onClick={() => onClosureClick(item._id)}
              />
            ) : item.type === CalendarItemType.EventSale &&
              onEventSaleClick &&
              onAllEventAttendeesClick &&
              hasMultipleEventSessions ? (
              <Menu.Root
                trigger={
                  <Button
                    mode="light"
                    Icon={NavArrowRight}
                    size="md"
                    aria-label={t("SLIDEOUT.CHOOSE_ATTENDEE_SCOPE")}
                  />
                }
                positioning={{ placement: "bottom-end" }}
                onSelect={({ value }) => {
                  if (value === "ALL") onAllEventAttendeesClick(item);
                  else onEventSaleClick(item);
                }}
              >
                <Menu.Item value="ALL">
                  <Text>{t("SLIDEOUT.ALL_ATTENDEES_OPTION")}</Text>
                </Menu.Item>
                <Menu.Item value="SESSION">
                  <Text>{t("SLIDEOUT.SESSION_ATTENDEES_OPTION")}</Text>
                </Menu.Item>
              </Menu.Root>
            ) : item.type === CalendarItemType.EventSale && onEventSaleClick ? (
              <Button
                mode={"light"}
                Icon={NavArrowRight}
                size={"md"}
                onClick={() => onEventSaleClick(item)}
              />
            ) : null}
          </Flex>
        </Card.BodyContent>
      </Card.Body>
    </Card.Surface>
  );
};
